
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { Guest } from "@/hooks/use-event-form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

type TimeOfDay = Database["public"]["Enums"]["time_of_day"];

interface ContentGenerationSectionProps {
  preferredDates: Date[];
  preferredTimeOfDay: TimeOfDay[];
  locationType: 'specific' | 'flexible';
  preferredVenues: string[];
  locationNote: string;
  guestList: Guest[];
  canGenerate: boolean;
  onSuccess: (description: string, title: string) => void;
}

export const ContentGenerationSection = ({
  preferredDates,
  preferredTimeOfDay,
  locationType,
  preferredVenues,
  locationNote,
  guestList,
  canGenerate,
  onSuccess,
}: ContentGenerationSectionProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleGenerateDescription = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent any default form submission
    console.log("Generate button clicked. Can generate:", canGenerate);
    
    if (!canGenerate) {
      console.log("Generation disabled, returning early");
      return;
    }
    
    setIsGenerating(true);
    console.log("Sending request to generate-event-description with data:", {
      preferredDates: preferredDates.map(d => d.toISOString()),
      preferredTimeOfDay,
      locationType,
      preferredVenues: locationType === 'specific' ? preferredVenues : undefined,
      locationNote: locationType === 'flexible' ? locationNote : undefined,
      guests: guestList
    });
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-event-description', {
        body: {
          preferredDates: preferredDates.map(d => d.toISOString()),
          preferredTimeOfDay,
          locationType,
          preferredVenues: locationType === 'specific' ? preferredVenues : undefined,
          locationNote: locationType === 'flexible' ? locationNote : undefined,
          guests: guestList
        }
      });

      if (error) {
        console.error('Error from edge function:', error);
        throw error;
      }
      
      console.log("Edge function response:", data);
      
      if (data?.description && data?.title) {
        console.log("Generation successful, updating content with:", {
          description: data.description,
          title: data.title
        });
        onSuccess(data.description, data.title);
        
        toast({
          title: "Success",
          description: "Generated event content successfully!",
        });
      } else {
        console.error("Edge function returned success but missing data:", data);
        throw new Error("Invalid response format from edge function");
      }
    } catch (error) {
      console.error('Detailed error generating content:', error);
      console.log('Error type:', typeof error);
      console.log('Error stringify:', JSON.stringify(error, null, 2));
      
      // Check for specific error types
      if (error instanceof Error) {
        console.log('Error message:', error.message);
        console.log('Error stack:', error.stack);
      }
      
      toast({
        title: "Error",
        description: "Failed to generate content. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button 
      size="sm"
      variant="secondary"
      onClick={handleGenerateDescription}
      disabled={!canGenerate || isGenerating}
      className="ml-2"
    >
      {isGenerating ? (
        <>Generating...</>
      ) : (
        <>
          <Sparkles className="mr-1" />
          Generate
        </>
      )}
    </Button>
  );
};
